import { cn } from '@mint-lib/tw-utils';

type CircularIndicatorProps = {
  size?: 'large' | 'small' | 'xsmall';
  color?: 'primary' | 'secondary' | 'warning' | 'ai';
  value: number | string;
  determinate?: boolean;
  indicatorClassNames?: string;
  trackClassNames?: string;
} & React.ComponentProps<'div'>;

export function CircularIndicator({
  size = 'large',
  color = 'primary',
  value,
  determinate = false,
  indicatorClassNames,
  trackClassNames,
  ...props
}: CircularIndicatorProps) {
  const sizeMap = {
    large: 40,
    small: 24,
    xsmall: 16,
  };
  const strokeWidth = size === 'large' ? 4 : 2;
  const radius = (sizeMap[size] - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const indeterminateOffset = 0.25 * circumference;
  const offset = determinate
    ? circumference - (Number(value) / 100) * circumference
    : indeterminateOffset;

  return (
    <div
      {...props}
      data-slot="progress-circle"
      data-size={size}
      className={cn(
        'shrink-0 data-[size=large]:relative data-[size=large]:size-10',
        !determinate && 'animate-spin',
      )}
    >
      <svg
        data-size={size}
        className="inset-0 -rotate-90 data-[size=large]:absolute"
        width={sizeMap[size]}
        height={sizeMap[size]}
        viewBox={`0 0 ${sizeMap[size]} ${sizeMap[size]}`}
      >
        {color === 'ai' && (
          <defs>
            <linearGradient id="ai-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--ai-lightCyan)" />
              <stop offset="100%" stopColor="var(--ai-lightGreen)" />
            </linearGradient>
          </defs>
        )}
        <circle
          data-slot="progress-circle-track"
          cx={sizeMap[size] / 2}
          cy={sizeMap[size] / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className={cn('text-transparent-main', trackClassNames)}
        />
        <circle
          data-slot="progress-circle-indicator"
          data-size={size}
          data-color={color}
          cx={sizeMap[size] / 2}
          cy={sizeMap[size] / 2}
          r={radius}
          stroke={color === 'ai' ? 'url(#ai-gradient)' : 'currentColor'}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(
            'transition-all duration-300 ease-in-out data-[color=primary]:text-tertiary-dark data-[color=secondary]:text-secondary-main data-[color=warning]:text-warning-main',
            color === 'ai' && 'text-transparent-main',
            indicatorClassNames,
          )}
        />
      </svg>
    </div>
  );
}
